import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Edit the subject + message used when emailing completed checklists to clients.
export const ChecklistEmailTemplate = ({ open, onOpenChange }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("body");
  useEffect(() => {
    if (open) { client.get("/checklist-email/settings").then((r) => setS(r.data)).catch((e) => { toast.error(apiErr(e.response?.data?.detail)); onOpenChange(false); }); }
  }, [open, onOpenChange]);
  const save = async () => {
    setBusy(true);
    try { await client.put("/checklist-email/settings", { subject: s.subject, body: s.body, companyName: s.companyName, companyPhone: s.companyPhone, companyEmail: s.companyEmail, senderName: s.senderName, copyTo: s.copyTo }); toast.success("Email template saved"); onOpenChange(false); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="email-template-dialog">
        <DialogHeader>
          <DialogTitle>Client email & PDF settings</DialogTitle>
          <DialogDescription>Used when you email a completed checklist. Words in curly brackets are filled in automatically.</DialogDescription>
        </DialogHeader>
        {!s ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-4">
            <div><Label>Sender name (shown as "From" in the client's inbox)</Label><Input value={s.senderName} onChange={(e) => setS({ ...s, senderName: e.target.value })} className="mt-1.5 h-11" data-testid="email-sender-name" /></div>
            <div><Label>Send a copy of every email to (leave empty for no copy)</Label><Input type="email" value={s.copyTo} onChange={(e) => setS({ ...s, copyTo: e.target.value })} className="mt-1.5 h-11" placeholder="reports@icleannw.co.uk" data-testid="email-copy-to" /></div>
            <div><Label>Subject</Label><Input value={s.subject} onFocus={() => setTarget("subject")} onChange={(e) => setS({ ...s, subject: e.target.value })} className="mt-1.5 h-11" data-testid="email-template-subject" /></div>
            <div><Label>Message</Label><Textarea value={s.body} onFocus={() => setTarget("body")} onChange={(e) => setS({ ...s, body: e.target.value })} rows={9} className="mt-1.5" data-testid="email-template-body" /></div>
            <div className="flex flex-wrap gap-1.5">
              {s.placeholders.map((p) => (
                <button key={p} type="button" onClick={() => setS({ ...s, [target]: `${s[target]}{${p}}` })} data-testid={`email-ph-${p}`}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs text-slate-600 hover:border-brand/50">{`{${p}}`}</button>
              ))}
            </div>
            <p className="text-xs text-slate-500">Tap a word to add it to the {target === "subject" ? "subject" : "message"}. A "View checklist report (PDF)" button with the photos is always added below the message.</p>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <p className="text-sm font-medium text-slate-800">Company details on the PDF</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Company name</Label><Input value={s.companyName} onChange={(e) => setS({ ...s, companyName: e.target.value })} className="mt-1.5" data-testid="pdf-company-name" /></div>
                <div><Label>Phone</Label><Input value={s.companyPhone} onChange={(e) => setS({ ...s, companyPhone: e.target.value })} className="mt-1.5" data-testid="pdf-company-phone" /></div>
                <div><Label>Email</Label><Input value={s.companyEmail} onChange={(e) => setS({ ...s, companyEmail: e.target.value })} className="mt-1.5" data-testid="pdf-company-email" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="email-template-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="email-template-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save template"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
